import { useId, useMemo, useState } from 'react';
import { Icon } from './icons';

// The prototype's building blocks as React: page heading, toolbar controls, metrics, panels, tables, feedback.

export const money = (v, dec = 0, currency = 'USD') => (v == null || Number.isNaN(Number(v)) ? '—'
  : new Intl.NumberFormat('en-US', { style: 'currency', currency: (currency || 'USD').toUpperCase(), maximumFractionDigits: dec, minimumFractionDigits: dec }).format(Number(v)));
export const num = v => (v == null ? '—' : new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(Number(v)));
export const ratio = v => (v ? `${Number(v).toFixed(2)}×` : '—');
export const pct = (v, dec = 1) => (v == null || !Number.isFinite(v) ? '—' : `${(v * 100).toFixed(dec)}%`);
export const initials = (name, fallback = '?') => {
  const words = String(name || '').trim().split(/\s+/).filter(w => /^\p{L}/u.test(w));
  return words.length ? words.map(w => w[0]).slice(0, 2).join('').toUpperCase() : fallback;
};
export const readStore = (key, fallback) => { try { return JSON.parse(localStorage.getItem(`trace-${key}`)) ?? fallback; } catch { return fallback; } };
export const persist = (key, value) => { try { localStorage.setItem(`trace-${key}`, JSON.stringify(value)); } catch { /* private mode: the choice lasts for this session */ } };

export function PageHeading({ eyebrow, title, description, actions }) {
  return (
    <div className="page-heading">
      <div>{eyebrow && <div className="eyebrow">{eyebrow}</div>}<h1>{title}</h1>{description && <p>{description}</p>}</div>
      {actions && <div className="heading-actions">{actions}</div>}
    </div>
  );
}

export function Btn({ icon, children, primary = false, ghost = false, small = false, iconOnly = false, blue = false, className = '', type = 'button', ...props }) {
  const cls = ['btn', primary && 'primary', ghost && 'ghost', small && 'small', iconOnly && 'icon-btn', blue && 'blue-btn', className].filter(Boolean).join(' ');
  return <button type={type} className={cls} {...props}>{icon && <Icon name={icon} />}{children}</button>;
}

export function TextBtn({ icon, children, blue = false, className = '', ...props }) {
  return <button type="button" className={`text-btn${blue ? ' blue' : ''}${className ? ` ${className}` : ''}`} {...props}>{children}{icon && <Icon name={icon} small />}</button>;
}

export function SelectWrap({ label, prefix, icon, value, onChange, options, disabled = false, className = '', ...rest }) {
  return (
    <label className={`select-wrap${className ? ` ${className}` : ''}`}>
      {icon && <Icon name={icon} small />}{prefix && <span>{prefix}</span>}
      <select aria-label={label} value={value} disabled={disabled} onChange={e => onChange(e.target.value)} {...rest}>
        {options.map(o => <option key={String(o.value)} value={o.value} disabled={o.disabled}>{o.label}</option>)}
      </select>
      <Icon name="chevron" small className="chevron" />
    </label>
  );
}

export function Badge({ tone, dot = false, className = '', children, ...rest }) {
  const cls = ['badge', tone === 'good' && 'good', tone === 'blue' && 'blue-badge', tone === 'warn' && 'warn', tone === 'bad' && 'bad', className].filter(Boolean).join(' ');
  return <span className={cls} {...rest}>{dot && <span className="dot" />}{children}</span>;
}

export function StatusDot({ paused = false, bad = false, children }) {
  return <span className={`status${paused ? ' paused' : ''}${bad ? ' bad' : ''}`}><span className="dot" />{children}</span>;
}

export function Metrics({ cols, className = '', children }) {
  return <div className={`metrics${cols === 3 ? ' metrics-three' : cols === 2 ? ' metrics-two' : ''}${className ? ` ${className}` : ''}`}>{children}</div>;
}

// The prototype's metric cell: a label with an info tooltip, the figure beside a sparkline, a foot with the delta.
export function MetricCell({ label, sup, info, value, spark, foot, className = '' }) {
  return (
    <div className={`metric${className ? ` ${className}` : ''}`}>
      <div className="metric-label">{label}{sup && <sup className="type-sup" aria-label={`note ${sup}`}>{sup}</sup>}{info && <span className="info-icon" title={info}><Icon name="info" small /></span>}</div>
      <div className="metric-main"><span className="metric-value">{value ?? '—'}</span>{spark}</div>
      {foot && <div className="metric-foot">{foot}</div>}
    </div>
  );
}

// ↗ 15.4% vs. previous period — adverse when the move hurts, neutral when nothing can be said.
export function Delta({ value, adverseWhenUp = false, caption = 'vs. previous period' }) {
  if (value == null || !Number.isFinite(value)) return <><span className="delta neutral">—</span><span>{caption}</span></>;
  const up = value >= 0;
  const adverse = adverseWhenUp ? up && value !== 0 : !up;
  return <><span className={`delta${adverse ? ' adverse' : ''}${value === 0 ? ' neutral' : ''}`}>{up ? '↗' : '↘'} {Math.abs(value * 100).toFixed(1)}%</span><span>{caption}</span></>;
}

// The 84×36 sparkline from the prototype: a line (or bars) through the last twelve points.
export function Spark({ values = [], bars = false, color }) {
  const points = values.length ? values.slice(-12) : [];
  if (points.length < 2) return <svg className={bars ? 'mini-bars' : 'metric-spark'} viewBox="0 0 84 36" fill="none" aria-hidden="true" />;
  const max = Math.max(...points, 1);
  const step = bars ? 7 : 84 / (points.length - 1);
  const h = v => Math.max(3, (v / max) * 32);
  return (
    <svg className={bars ? 'mini-bars' : 'metric-spark'} viewBox="0 0 84 36" fill="none" aria-hidden="true">
      {bars ? points.map((v, i) => <rect key={i} x={i * step} y={36 - h(v)} width="4" height={h(v)} fill={color || '#89a4f4'} />)
        : <path d={points.map((v, i) => `${i ? 'L' : 'M'}${(i * step).toFixed(1)} ${(36 - h(v)).toFixed(1)}`).join(' ')} stroke={color || '#5884ed'} strokeWidth="1.5" />}
    </svg>
  );
}

export function Panel({ className = '', children, ...rest }) {
  return <section className={`panel${className ? ` ${className}` : ''}`} {...rest}>{children}</section>;
}
export function PanelHead({ title, description, children }) {
  return <div className="panel-head"><div>{title && <h2>{title}</h2>}{description && <p>{description}</p>}</div>{children}</div>;
}
export function PanelBody({ className = '', children }) { return <div className={`panel-body${className ? ` ${className}` : ''}`}>{children}</div>; }
export function PanelFooter({ children }) { return <div className="panel-footer">{children}</div>; }

export function Segmented({ label, value, options, onChange }) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map(o => <button key={String(o.value)} type="button" className={value === o.value ? 'selected' : ''} aria-pressed={value === o.value} onClick={() => onChange(o.value)}>{o.label}</button>)}
    </div>
  );
}

export function Toolbar({ left, right }) {
  return <div className="toolbar"><div className="toolbar-side">{left}</div><div className="toolbar-side">{right}</div></div>;
}
export function TableToolbar({ children }) { return <div className="table-toolbar">{children}</div>; }
export function TableTabs({ tabs, active, onChange, label = 'Table views', trailing }) {
  return (
    <div className="table-tabs" role="group" aria-label={label}>
      {tabs.map(t => <button key={t.id} type="button" className={active === t.id ? 'selected' : ''} aria-pressed={active === t.id} onClick={() => onChange(t.id)}>{t.label}{t.count != null && <span className="count">{t.count}</span>}</button>)}
      {trailing}
    </div>
  );
}
export function SearchInput({ label, value, onChange, placeholder = 'Search…', id, ...rest }) {
  const auto = useId();
  return (
    <label className="search-input">
      <Icon name="search" />
      <input id={id || auto} aria-label={label} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} {...rest} />
    </label>
  );
}

export function TableBottom({ children }) { return <div className="table-bottom">{children}</div>; }
export function Pagination({ page, pages, onChange }) {
  const items = useMemo(() => {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    const set = new Set([1, pages, page - 1, page, page + 1].filter(n => n >= 1 && n <= pages));
    const list = [...set].sort((a, b) => a - b);
    return list.flatMap((n, i) => (i && n - list[i - 1] > 1 ? ['…', n] : [n]));
  }, [page, pages]);
  return (
    <div className="pagination">
      <button type="button" aria-label="Previous page" disabled={page <= 1} onClick={() => onChange(page - 1)}><Icon name="left" small /></button>
      {items.map((n, i) => (n === '…' ? <span key={`e${i}`} className="muted">…</span>
        : <button key={n} type="button" className={n === page ? 'current' : ''} aria-current={n === page ? 'page' : undefined} aria-label={`Page ${n}`} onClick={() => onChange(n)}>{n}</button>))}
      <button type="button" aria-label="Next page" disabled={page >= pages} onClick={() => onChange(page + 1)}><Icon name="right" small /></button>
    </div>
  );
}

// Client-side paging for the prototype's tables: "Showing 1–8 of 12 campaigns" with the page squares.
export function usePaging(total, pageSize) {
  const [page, setPage] = useState(1);
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pages);
  return { page: current, pages, setPage, slice: rows => rows.slice((current - 1) * pageSize, current * pageSize),
    summary: noun => (total ? `Showing ${(current - 1) * pageSize + 1}–${Math.min(current * pageSize, total)} of ${total} ${noun}` : `No matching ${noun}`) };
}

export function EmptyState({ icon = 'search', title, children, action, className = '' }) {
  return <div className={`empty-state${className ? ` ${className}` : ''}`} role="status"><Icon name={icon} /><h3>{title}</h3>{children && <p>{children}</p>}{action}</div>;
}
export function QueryFailure({ onRetry, message = 'Could not load this view. Your data has not been changed.', busy = false }) {
  return <div className="empty-state" role="alert"><Icon name="info" /><h3>Something went wrong</h3><p>{message}</p>{onRetry && <Btn onClick={onRetry} disabled={busy} aria-busy={busy || undefined}>{busy ? 'Retrying…' : 'Try again'}</Btn>}</div>;
}
export function Loading({ children = 'Loading…' }) { return <div className="empty-state" role="status"><p>{children}</p></div>; }

export function SettingsNote({ icon = 'info', children }) { return <div className="settings-note"><Icon name={icon} small /> {children}</div>; }
export function Notice({ icon, className = '', children }) { return <div className={`notice${className ? ` ${className}` : ''}`}>{icon && <><Icon name={icon} small /> </>}{children}</div>; }
export function DetailRow({ label, children, strong = false }) {
  return <div className="detail-row"><span>{label}</span>{strong ? <strong style={{ fontWeight: 500 }}>{children}</strong> : <span>{children}</span>}</div>;
}
export function Divider() { return <div className="divider" />; }
export function Label({ blue = false, children }) { return <span className={`label${blue ? ' blue' : ''}`}>{children}</span>; }
export function Avatar({ name, size, className = '' }) {
  const style = size ? { width: size, height: size, fontSize: size > 40 ? 13 : 12 } : undefined;
  return <span className={`avatar${className ? ` ${className}` : ''}`} style={style} aria-hidden="true">{initials(name, '?')}</span>;
}
export function FormField({ label, hint, optional = false, children }) {
  return <label className="form-field">{label}{optional && <span className="muted"> (optional)</span>}{children}{hint && <small>{hint}</small>}</label>;
}
export function Switch({ checked, onChange, label, disabled = false }) {
  return <label className="switch"><input type="checkbox" checked={checked} disabled={disabled} aria-label={label} onChange={e => onChange(e.target.checked)} /><span /></label>;
}
